// @flow
import difference from 'lodash/difference';
import mapKeys from 'lodash/mapKeys';
import keys from 'lodash/keys';
import has from 'lodash/has';
import forEach from 'lodash/forEach';
import indexOf from 'lodash/indexOf';
import isFunction from 'lodash/isFunction';
import moment from 'moment-timezone';

import WebNotificationService, {
    WebNotificationState
} from 'common/services/WebNotificationService';
import { EventTypeEnum, EventStatusEnum } from 'common/enums/EventEnums';
import TimeUtils from 'common/utils/TimeUtils';
import StringUtils from 'common/utils/StringUtils';
import merge from 'lodash/merge';
import queryString from 'query-string';
import history from 'common/middleware/history';

const CollectionTypes = {
    Event: 'event',
    Group: 'group'
};
const singleton = Symbol();
const singletonEnforcer = Symbol();

class EventNotificationService extends WebNotificationService {
    constructor(enforcer) {
        super();
        if (enforcer !== singletonEnforcer) {
            throw 'Cannot construct singleton use the instance method instead';
        }
    }

    static get instance() {
        if (!this[singleton]) {
            this[singleton] = new EventNotificationService(singletonEnforcer);
        }
        return this[singleton];
    }

    setRouterFunctionsAndBoundActionCreators = references => {
        forEach(references, (reference, referenceName) => {
            if (isFunction(reference)) {
                this[referenceName] = reference;
            }
        });
    };

    setPreviousEventsListHandler = eventsList => {
        this.setPreviousEventsList(eventsList);
    };

    setAppPreferencesGetter = getAppPreferences =>
        (this.getAppPreferences = getAppPreferences);

    getNotificationState = () => {
        const appPreferences = this.getAppPreferences();
        if (appPreferences && appPreferences.eventNotifications) {
            const { eventNotifications } = appPreferences;
            return eventNotifications.notificationState || 'off';
        }
        return 'off';
    };

    setNotificationStateHandler = permission => {
        this.setNotificationState(permission);
    };

    disableNotificationState = () => {
        this.setNotificationState(WebNotificationState.PERMISSION_DENIED);
    };

    enableNotificationState = () => {
        this.setNotificationState(WebNotificationState.PERMISSION_GRANTED);
    };

    // events are based on groups also as there can be multiple eventGroups with the same eventType
    // this could be a bug.
    getKeyName = (event, type = CollectionTypes.Event) => {
        switch (type) {
            case CollectionTypes.Event: {
                const breachedSince = moment(event.breachedSince);
                if (breachedSince.isValid()) {
                    return `${event.clientId}-${
                        event.eventId
                    }-${breachedSince.unix()}`;
                }
                return `${event.clientId}-${event.eventId}`;
            }
            case CollectionTypes.Group: {
                return `${event.eventType}-${event.definitionId}`;
            }
        }
    };

    goToUserTourScreen = (
        location,
        clientId,
        userId,
        refToTaskId,
        date,
        tourId
    ) => {
        var path = `/client/${clientId}/user/${userId}`;
        var { query } = location;
        delete query.selectedTaskId;
        if (refToTaskId) {
            query.refToTaskId = refToTaskId;
        }
        if (date) {
            query.date = date;
        }
        if (tourId) {
            query.tourId = tourId;
        }
        var newLocation = {
            pathname: path,
            search: queryString.stringify(query)
        };
        history.push(newLocation);
    };

    goToTaskScreen = (location, selectedTaskId) => {
        var query;
        if (selectedTaskId) {
            query = merge(location.query, {
                selectedTaskId
            });
        } else {
            delete location.query.selectedTaskId;
            delete location.query.assignListOpen;
            ({ query } = location);
        }

        var { pathname } = location;
        var path = {
            pathname,
            search: queryString.stringify(query)
        };
        history.push(path);
    };

    getBoundRouterForEventType = (eventType, location, clientId, sourceId) => {
        switch (eventType) {
            case EventTypeEnum.UserLocGap:
            case EventTypeEnum.UserBatterLevel: {
                return this.goToUserTourScreen(
                    location,
                    clientId,
                    sourceId,
                    undefined
                );
            }
            case EventTypeEnum.TaskAcceptDelay:
            case EventTypeEnum.TaskLocFarArrived:
            case EventTypeEnum.TaskTemperatureBreach:
            case EventTypeEnum.TaskEtaDelayCompleted:
            case EventTypeEnum.TaskSlaDelayCompleted:
            case EventTypeEnum.VehicleFreezerTemperature: {
                return this.goToTaskScreen(location, sourceId, clientId);
            }
            default:
                return null;
        }
    };

    filterEvents = (events, previousEvents = {}) => {
        const notificationShownAt = this.getNotificationShownAtTime();
        const previousEventsList = this.getPreviousEventsList();
        let filteredEvents = {};

        forEach(events, (event, eventKey) => {
            const eventKeyName = this.getKeyName(event);
            if (
                event.status === EventStatusEnum.Breached &&
                !has(previousEvents, eventKeyName) &&
                indexOf(previousEventsList, eventKeyName) === -1 &&
                (TimeUtils.isSameOrAfter(
                    event.breachedSince,
                    notificationShownAt
                ) ||
                    StringUtils.isEmpty(notificationShownAt))
            ) {
                filteredEvents = {
                    ...filteredEvents,
                    [eventKey]: event
                };
            }
        });
        return filteredEvents;
    };

    getPublishableEvents = (previousEvents, nextEvents) => {
        const groupedPrev = mapKeys(previousEvents, p =>
            this.getKeyName(p, CollectionTypes.Group)
        );
        const groupedNext = mapKeys(nextEvents, n =>
            this.getKeyName(n, CollectionTypes.Group)
        );
        const prevKeys = keys(groupedPrev);
        const nextKeys = keys(groupedNext);
        const uncommonEventGroups = difference(nextKeys, prevKeys);
        const commonEventGroups = difference(nextKeys, uncommonEventGroups);
        let publishableEvents = {};
        let nextEventsList = [];

        //send all events in case of new event group.
        forEach(uncommonEventGroups, uncommonEventGroup => {
            const { events: uncommonNextEvents } = groupedNext[
                uncommonEventGroup
            ];
            const groupedUncommonNextEvents = mapKeys(uncommonNextEvents, n =>
                this.getKeyName(n)
            );
            nextEventsList = [
                ...nextEventsList,
                ...keys(groupedUncommonNextEvents)
            ];
            publishableEvents = {
                ...publishableEvents,
                ...this.filterEvents(groupedUncommonNextEvents)
            };
        });

        //diff existing events
        forEach(commonEventGroups, commonEvent => {
            const { events: commonNextEvents } = groupedNext[commonEvent];
            const { events: commonPrevEvents } = groupedPrev[commonEvent];
            const groupedPreviousEvents = mapKeys(commonNextEvents, p =>
                this.getKeyName(p)
            );
            const groupedNextEvents = mapKeys(commonPrevEvents, n =>
                this.getKeyName(n)
            );
            nextEventsList = [...nextEventsList, ...keys(groupedNextEvents)];
            publishableEvents = {
                ...publishableEvents,
                ...this.filterEvents(groupedNextEvents, groupedPreviousEvents)
            };
        });
        this.setPreviousEventsListHandler(nextEventsList);
        return publishableEvents;
    };

    getNotificationText = event => {
        const {
            eventType,
            entity: { eventText, userName, sourceId }
        } = event;
        switch (eventType) {
            case EventTypeEnum.TaskSlaDelayCompleted: {
                return `${sourceId} ${eventText.toLowerCase()}`;
            }
            case EventTypeEnum.UserBatterLevel: {
                return `${userName} has ${eventText.toLowerCase()} remaining`;
            }
            case EventTypeEnum.UserLocGap: {
                return `${userName} ${eventText.toLowerCase()}`;
            }
            case EventTypeEnum.VehicleFreezerTemperature: {
                return `${eventText.toLowerCase()} for ${sourceId}`;
            }
            default: {
                return eventText.toLowerCase();
            }
        }
    };

    getSourceAndClientId = event => {
        const {
            entity: { sourceId },
            clientId
        } = event;
        return { sourceId, clientId };
    };

    getPreviousEventsList = () => {
        const appPreferences = this.getAppPreferences();
        if (appPreferences && appPreferences.eventNotifications) {
            const { eventNotifications } = appPreferences;
            return eventNotifications.eventsList || [];
        }
        return [];
    };

    isNotificationsPreviouslyShown = () => {
        const appPreferences = this.getAppPreferences();
        if (appPreferences && appPreferences.eventNotifications) {
            const { eventNotifications } = appPreferences;
            return !!eventNotifications.isNotificationsPreviouslyShown;
        }
        return false;
    };

    getNotificationShownAtTime = () => {
        const appPreferences = this.getAppPreferences();
        if (appPreferences && appPreferences.eventNotifications) {
            const { eventNotifications } = appPreferences;
            return eventNotifications.shownAt;
        }
        return null;
    };

    process = (mutableNextEventGroup, getState, params, location) => {
        const state = getState();
        const { date, carrierTeam, userId } = params;
        const today = moment();
        const expectedDate = moment(date);
        const notificationState = this.getNotificationState();
        const isNotificationsShown = this.isNotificationsPreviouslyShown();

        if (expectedDate.isSame(today, 'day') && notificationState === 'on') {
            const prevEventGroup = state.eventGroup.getIn([
                'records',
                params.clientId,
                date,
                carrierTeam,
                userId
            ]);
            let mutablePrevEventGroup = [];
            if (!isNotificationsShown && prevEventGroup) {
                this.setNotificationPreviouslyShown(true);
                mutablePrevEventGroup = prevEventGroup.toJS();
            }
            const allEvents = this.getPublishableEvents(
                mutablePrevEventGroup,
                mutableNextEventGroup
            );
            this.setNotificationShownAtTime();

            if (isNotificationsShown) {
                forEach(allEvents, event => {
                    const { eventName, eventType } = event;
                    const eventBody = this.getNotificationText(event);
                    const { clientId, sourceId } = this.getSourceAndClientId(
                        event
                    );
                    const boundRouters = this.getBoundRouterForEventType(
                        eventType,
                        location,
                        clientId,
                        sourceId
                    );
                    this.spawnNotification(eventName, eventBody, undefined, {
                        clientId,
                        sourceId,
                        boundRouters
                    });
                });
            }
        }
    };
}

const { instance: EventNotificationServiceInstance } = EventNotificationService;
export default EventNotificationServiceInstance;
